import { useContext, useEffect, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@adflow/utils';
import { IMarketingCloudUser } from '@ads/iam-library';
import { generateUserToCheck } from '../utils/user-context';

interface ProtectedRouteProps {
  children: ReactNode;
  checkPermission: (user: IMarketingCloudUser | null) => boolean | Promise<boolean>;
  redirectTo?: string;
  fallback?: ReactNode;
}

export function ProtectedRoute({
  children,
  checkPermission,
  redirectTo = '/no-access',
  fallback = null,
}: ProtectedRouteProps) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [authorizedRequest, setAuthorizedRequest] = useState<{
    user: unknown;
    checkPermission: ProtectedRouteProps['checkPermission'];
    redirectTo: string;
  } | null>(null);

  useEffect(() => {
    let isActive = true;

    const checkAccess = async () => {
      try {
        const userToCheck = generateUserToCheck(user);

        if (!userToCheck) {
          if (isActive) {
            navigate(redirectTo);
          }
          return;
        }

        const permission = await checkPermission(userToCheck);

        if (!isActive) {
          return;
        }

        if (!permission) {
          navigate(redirectTo);
          return;
        }

        setAuthorizedRequest({ user, checkPermission, redirectTo });
      } catch (error) {
        console.error('Failed to check route permission', error);
        if (isActive) {
          navigate(redirectTo);
        }
      }
    };

    checkAccess();

    return () => {
      isActive = false;
    };
  }, [user, navigate, redirectTo, checkPermission]);

  const isAuthorizedForCurrentRequest =
    authorizedRequest !== null &&
    authorizedRequest.user === user &&
    authorizedRequest.checkPermission === checkPermission &&
    authorizedRequest.redirectTo === redirectTo;

  if (!isAuthorizedForCurrentRequest) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
